// PropertyStep.js — Mortgage Matrix Broker CRM
// Version D.9.1.2 (button-style card selectors update)
// -------------------------------------------------------------------------------------------------
// ✅ Deposit Amount is written to crmData.assetsLiabilities.assets as a Savings asset (metaKey = deposit_savings)
// ✅ ALSO hydrates depositAmount back from that asset if depositAmount is blank (snapshot/mobile hydrate safe)
// ✅ Updated choice controls to feel more like buttons (less radius, clearer selected state)
// ✅ “Do you own any other property?” (Yes/No)
// ✅ If Yes → shows address immediately (auto-creates Property 1) + option to “+ Add another property”
// ✅ Purchase: only show other-property question if NOT first home buyer
// ✅ Refinance: always show other-property question
// ✅ Data stored under crmData.property: hasOtherProperty + otherProperties[]
// ✅ Local Back / Next removed (shared nav handled in BrokerCRM)
//
// NOTE: make sure you pass crmData.property into AssetsLiabilities so Home Loan liabilities can link to addresses.

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"
import GooglePlacesAutocomplete, { geocodeByPlaceId } from "react-google-places-autocomplete"

// -------------------------------------------------------------
// Mercury Opportunity.transactionType → UI applicationType
// -------------------------------------------------------------
const mapMercuryTransactionTypeToAppType = (t) => {
  const v = String(t || "").trim().toLowerCase()
  if (v === "refinance") return "refinance"
  if (v === "purchase") return "purchase"
  return ""
}

// UI applicationType → Mercury transactionType (pretty-cased)
const mapAppTypeToMercuryTransactionType = (appType) => {
  if (appType === "refinance") return "Refinance"
  if (appType === "purchase") return "Purchase"
  return ""
}

// -------------------------------------------------------------
// Normalise incoming address from parent/mobile-search
// -------------------------------------------------------------
const normaliseIncomingAddress = (addr) => {
  if (!addr) return null
  if (typeof addr === "string") {
    const s = addr.trim()
    return s ? { formatted: s } : null
  }
  if (typeof addr === "object") {
    if (addr.formatted) return addr
    const maybe = addr.address || addr.formatted_address || addr.description
    if (typeof maybe === "string" && maybe.trim()) return { ...addr, formatted: maybe.trim() }
    return addr
  }
  return null
}

// Numeric helper (safe)
const toNumberSafe = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.]/g, ""))
  return Number.isFinite(n) ? n : 0
}

// -------------------------------------------------------------
// Alias helpers (accept both MobileStep + Lambda naming styles)
// -------------------------------------------------------------
const pick = (...vals) => {
  for (const v of vals) {
    if (!(v === undefined || v === null || v === "")) return v
  }
  return ""
}

const normaliseOccupancy = (v) => {
  const s = String(v || "").trim().toLowerCase()
  if (!s) return ""
  if (s.includes("invest")) return "investment"
  if (s.includes("live") || s.includes("owner") || s.includes("occup")) return "livein"
  if (s === "livein" || s === "investment") return s
  return s
}

const normaliseYesNo = (v) => {
  const s = String(v || "").trim().toLowerCase()
  if (!s) return ""
  if (s === "y" || s === "yes" || s === "true" || s === "1") return "yes"
  if (s === "n" || s === "no" || s === "false" || s === "0") return "no"
  return s
}

// Other properties normaliser
const normaliseOtherProps = (arr) => {
  if (!Array.isArray(arr)) return []
  return arr
    .map((p) => ({
      address: normaliseIncomingAddress(p?.address || p?.propertyAddress || p?.addr),
      hasRentalIncome: normaliseYesNo(p?.hasRentalIncome || p?.rental || p?.tenanted),
      rentalIncomeMonthly: String(p?.rentalIncomeMonthly ?? p?.rentMonthly ?? "").replace(/[^\d]/g, ""),
    }))
    .filter((p) => p.address?.formatted || p.rentalIncomeMonthly || p.hasRentalIncome)
}

const blankOtherProperty = () => ({
  address: null,
  hasRentalIncome: "",
  rentalIncomeMonthly: "",
})

function PropertyStep({
  data = {},
  assetsLiabilities = { assets: [], liabilities: [] },
  updateSection, // expects (sectionName, value)
}) {
  // --------------------------------
  // Touch tracking (prevents overwrite)
  // --------------------------------
  const touchedRef = useRef({})
  const markTouched = (key) => {
    touchedRef.current[key] = true
  }

  // --------------------------------
  // Formatting helpers
  // --------------------------------
  const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "")

  const formatThousands = (v) => {
    const raw = digitsOnly(v)
    if (!raw) return ""
    return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  const percentSanitize = (v) => {
    let s = String(v ?? "").replace(/[^\d.]/g, "")
    const firstDot = s.indexOf(".")
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "")
      const [a, b = ""] = s.split(".")
      s = `${a}.${b.slice(0, 2)}`
    }
    return s.slice(0, 6)
  }

  // --------------------------------
  // Deposit → Assets helper (Savings)
  // --------------------------------
  const DEPOSIT_KEY = "deposit_savings"

  const getDepositFromAssets = useCallback(() => {
    const assets = Array.isArray(assetsLiabilities?.assets) ? assetsLiabilities.assets : []
    const a = assets.find((x) => x?.metaKey === DEPOSIT_KEY)
    if (!a) return ""
    const v = digitsOnly(a.value ?? a.amount ?? "")
    return v || ""
  }, [assetsLiabilities])

  const upsertDepositAsSavingsAsset = useCallback((currentAssets = [], depositDigits) => {
    const safeAssets = Array.isArray(currentAssets) ? [...currentAssets] : []
    const amountDigits = String(depositDigits ?? "").replace(/[^\d]/g, "")
    const amountNum = amountDigits ? Number(amountDigits) : 0

    if (!amountNum) {
      return safeAssets.filter((a) => a?.metaKey !== DEPOSIT_KEY)
    }

    const idx = safeAssets.findIndex((a) => a?.metaKey === DEPOSIT_KEY)

    const depositAsset = {
      type: "Savings",
      value: String(amountNum),
      metaKey: DEPOSIT_KEY,
      label: "Deposit Amount",
      details: "Captured from Property step",
      basis: "Applicant",
      ownership: 100,
      applicant: 1,
    }

    if (idx >= 0) safeAssets[idx] = { ...safeAssets[idx], ...depositAsset }
    else safeAssets.unshift(depositAsset)

    return safeAssets
  }, [])

  const lastDepositAssetSigRef = useRef("")
  const pushDepositAssetToParent = useCallback(
    (depositDigits) => {
      if (typeof updateSection !== "function") return

      const currentAssets = Array.isArray(assetsLiabilities?.assets) ? assetsLiabilities.assets : []
      const currentLiabs = Array.isArray(assetsLiabilities?.liabilities) ? assetsLiabilities.liabilities : []

      const nextAssets = upsertDepositAsSavingsAsset(currentAssets, depositDigits)

      const sig = JSON.stringify(nextAssets)
      if (sig === lastDepositAssetSigRef.current) return
      lastDepositAssetSigRef.current = sig

      updateSection("assetsLiabilities", { assets: nextAssets, liabilities: currentLiabs })
    },
    [updateSection, assetsLiabilities, upsertDepositAsSavingsAsset]
  )

  // --------------------------------
  // Local state
  // --------------------------------
  const [formData, setFormData] = useState(() => {
    const depositFromAssets = getDepositFromAssets()
    const otherProps = normaliseOtherProps(pick(data.otherProperties, data.other_props, []))

    return {
      applicationType: data.applicationType || mapMercuryTransactionTypeToAppType(data.transactionType) || "",
      transactionType: data.transactionType || mapAppTypeToMercuryTransactionType(data.applicationType) || "",

      foundProperty: normaliseYesNo(pick(data.foundProperty, data.hasFoundProperty, data.found_property)) || "",
      isFirstHomeBuyer: normaliseYesNo(pick(data.isFirstHomeBuyer, data.firstHomeBuyer, data.first_home_buyer)) || "",
      occupancy: normaliseOccupancy(pick(data.occupancy, data.propertyUse, data.use)) || "",

      propertyAddress: normaliseIncomingAddress(data.propertyAddress),

      rentalIncomeWeekly: data.rentalIncomeWeekly || "",

      purchasePrice: data.purchasePrice || "",
      depositAmount: digitsOnly(data.depositAmount || depositFromAssets || ""),

      propertyValue: data.propertyValue || "",
      loanBalance: data.loanBalance || "",
      interestRate: data.interestRate || "",
      currentLender: data.currentLender || "",

      hasOtherProperty: normaliseYesNo(pick(data.hasOtherProperty, data.otherProperty, data.has_other_property)) || "",
      otherProperties: otherProps,
    }
  })

  const setField = (key, value) => {
    markTouched(key)
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const isPurchase = formData.applicationType === "purchase"
  const isRefinance = formData.applicationType === "refinance"

  // --------------------------------
  // Debounced + blur flush sync
  // --------------------------------
  const syncTimerRef = useRef(null)
  const lastPayloadRef = useRef("")

  const flushSync = useCallback(
    (nextData) => {
      if (typeof updateSection !== "function") return

      const payload = JSON.stringify(nextData)
      if (payload === lastPayloadRef.current) return
      lastPayloadRef.current = payload

      updateSection("property", nextData)

      if (nextData?.applicationType === "purchase") {
        pushDepositAssetToParent(nextData?.depositAmount)
      } else {
        pushDepositAssetToParent("")
      }
    },
    [updateSection, pushDepositAssetToParent]
  )

  useEffect(() => {
    if (typeof updateSection !== "function") return
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current)

    syncTimerRef.current = setTimeout(() => {
      flushSync(formData)
    }, 350)

    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
    }
  }, [formData, flushSync, updateSection])

  const setFormAndFlush = (updater) => {
    setFormData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater
      flushSync(next)
      return next
    })
  }

  // --------------------------------
  // Address Select Sync (controlled)
  // --------------------------------
  const addressTouchedRef = useRef(false)
  const [addressSelectValue, setAddressSelectValue] = useState(null)

  const resetAddressControl = useCallback(() => {
    addressTouchedRef.current = false
    touchedRef.current.propertyAddress = false
    setAddressSelectValue(null)
  }, [])

  useEffect(() => {
    if (addressTouchedRef.current) return

    const formatted = formData.propertyAddress?.formatted
    if (!formatted) {
      setAddressSelectValue(null)
      return
    }

    setAddressSelectValue({
      label: formatted,
      value: {
        description: formatted,
        place_id: formData.propertyAddress?.place_id || null,
      },
    })
  }, [formData.propertyAddress])

  const handleAddressSelect = async (selected) => {
    if (!selected) {
      addressTouchedRef.current = true
      markTouched("propertyAddress")
      const next = { ...formData, propertyAddress: null }
      setAddressSelectValue(null)
      setFormData(next)
      flushSync(next)
      return
    }

    addressTouchedRef.current = true
    markTouched("propertyAddress")
    setAddressSelectValue(selected)

    const placeId = selected?.value?.place_id

    if (!placeId) {
      const next = { ...formData, propertyAddress: { formatted: selected.label } }
      setFormData(next)
      flushSync(next)
      return
    }

    try {
      const results = await geocodeByPlaceId(placeId)
      const r = results[0]
      const components = r.address_components || []
      const get = (t) => components.find((c) => c.types?.includes(t))?.long_name || ""

      const structured = {
        formatted: r.formatted_address,
        place_id: placeId,
        street: `${get("street_number")} ${get("route")}`.trim(),
        suburb: get("locality") || get("sublocality") || get("administrative_area_level_2"),
        state: get("administrative_area_level_1"),
        postcode: get("postal_code"),
        lat: r.geometry?.location?.lat?.(),
        lng: r.geometry?.location?.lng?.(),
      }

      const next = { ...formData, propertyAddress: structured }
      setFormData(next)
      flushSync(next)
    } catch (err) {
      console.error("❌ Address Error:", err)
      const next = { ...formData, propertyAddress: { formatted: selected.label, place_id: placeId } }
      setFormData(next)
      flushSync(next)
    }
  }

  // --------------------------------
  // Hydrate when data changes
  // --------------------------------
  const lastHydrateSigRef = useRef("")
  useEffect(() => {
    const sig = JSON.stringify({
      applicationType: data.applicationType ?? "",
      transactionType: data.transactionType ?? "",
      foundProperty: data.foundProperty ?? "",
      hasFoundProperty: data.hasFoundProperty ?? "",
      found_property: data.found_property ?? "",
      isFirstHomeBuyer: data.isFirstHomeBuyer ?? "",
      firstHomeBuyer: data.firstHomeBuyer ?? "",
      first_home_buyer: data.first_home_buyer ?? "",
      occupancy: data.occupancy ?? "",
      propertyUse: data.propertyUse ?? "",
      use: data.use ?? "",
      propertyAddress: data.propertyAddress ?? null,
      rentalIncomeWeekly: data.rentalIncomeWeekly ?? "",
      purchasePrice: data.purchasePrice ?? "",
      depositAmount: data.depositAmount ?? "",
      propertyValue: data.propertyValue ?? "",
      loanBalance: data.loanBalance ?? "",
      interestRate: data.interestRate ?? "",
      currentLender: data.currentLender ?? "",
      depositFromAssets: getDepositFromAssets() ?? "",
      hasOtherProperty: data.hasOtherProperty ?? "",
      otherProperties: data.otherProperties ?? data.other_props ?? [],
    })

    if (sig === lastHydrateSigRef.current) return
    lastHydrateSigRef.current = sig

    const hasMeaningful = (v) => !(v === undefined || v === null || v === "")

    setFormData((prev) => {
      const next = { ...prev }

      const setIfUntouched = (key, value) => {
        if (touchedRef.current[key]) return
        if (!hasMeaningful(value)) return
        next[key] = value
      }

      const hydratedAppType =
        (touchedRef.current.applicationType ? prev.applicationType : data.applicationType) ||
        mapMercuryTransactionTypeToAppType(touchedRef.current.transactionType ? prev.transactionType : data.transactionType) ||
        prev.applicationType ||
        ""

      setIfUntouched("applicationType", hydratedAppType)

      const desiredTranx =
        (hasMeaningful(data.transactionType) ? data.transactionType : "") ||
        mapAppTypeToMercuryTransactionType(hydratedAppType) ||
        prev.transactionType ||
        ""
      setIfUntouched("transactionType", desiredTranx)

      setIfUntouched("foundProperty", normaliseYesNo(pick(data.foundProperty, data.hasFoundProperty, data.found_property)))
      setIfUntouched("isFirstHomeBuyer", normaliseYesNo(pick(data.isFirstHomeBuyer, data.firstHomeBuyer, data.first_home_buyer)))
      setIfUntouched("occupancy", normaliseOccupancy(pick(data.occupancy, data.propertyUse, data.use)))

      const incomingAddr = normaliseIncomingAddress(data.propertyAddress)
      if (!touchedRef.current.propertyAddress && incomingAddr?.formatted) {
        next.propertyAddress = incomingAddr
      }

      setIfUntouched("rentalIncomeWeekly", data.rentalIncomeWeekly)
      setIfUntouched("purchasePrice", data.purchasePrice)

      if (!touchedRef.current.depositAmount) {
        const fromProp = digitsOnly(data.depositAmount || "")
        const fromAssets = getDepositFromAssets()
        const resolved = fromProp || fromAssets || ""
        if (resolved) next.depositAmount = resolved
      }

      setIfUntouched("propertyValue", data.propertyValue)
      setIfUntouched("loanBalance", data.loanBalance)
      setIfUntouched("interestRate", data.interestRate)
      setIfUntouched("currentLender", data.currentLender)

      if (!touchedRef.current.hasOtherProperty) {
        const v = normaliseYesNo(pick(data.hasOtherProperty, data.otherProperty, data.has_other_property))
        if (v) next.hasOtherProperty = v
      }
      if (!touchedRef.current.otherProperties) {
        const arr = normaliseOtherProps(pick(data.otherProperties, data.other_props, []))
        if (arr.length) next.otherProperties = arr
      }

      return next
    })
  }, [data, assetsLiabilities, getDepositFromAssets])

  const didInitialDepositSyncRef = useRef(false)
  useEffect(() => {
    if (!isPurchase) return
    if (didInitialDepositSyncRef.current) return

    const d = digitsOnly(formData.depositAmount || "")
    if (!d) return

    const fromAssets = getDepositFromAssets()
    if (fromAssets !== d) {
      pushDepositAssetToParent(d)
    }

    didInitialDepositSyncRef.current = true
  }, [isPurchase, formData.depositAmount, getDepositFromAssets, pushDepositAssetToParent])

  const handleFoundPropertyChange = (v) => {
    markTouched("foundProperty")
    const next = { ...formData, foundProperty: v }

    if (v === "no") {
      resetAddressControl()
      next.propertyAddress = null
    }

    setFormData(next)
    flushSync(next)
  }

  // --------------------------------
  // OTHER PROPERTIES
  // --------------------------------
  const addOtherProperty = () => {
    markTouched("otherProperties")
    setFormAndFlush((prev) => ({
      ...prev,
      otherProperties: [...(Array.isArray(prev.otherProperties) ? prev.otherProperties : []), blankOtherProperty()],
    }))
  }

  const removeOtherProperty = (idx) => {
    markTouched("otherProperties")
    setFormAndFlush((prev) => {
      const arr = Array.isArray(prev.otherProperties) ? prev.otherProperties : []
      return { ...prev, otherProperties: arr.filter((_, i) => i !== idx) }
    })
  }

  const patchOtherProperty = (idx, patch) => {
    markTouched("otherProperties")
    setFormAndFlush((prev) => {
      const arr = Array.isArray(prev.otherProperties) ? [...prev.otherProperties] : []
      arr[idx] = { ...(arr[idx] || {}), ...patch }
      return { ...prev, otherProperties: arr }
    })
  }

  // --------------------------------
  // Purchase: if First Home Buyer = Yes, force "No other properties"
  // --------------------------------
  useEffect(() => {
    if (!isPurchase) return
    if (formData.isFirstHomeBuyer !== "yes") return

    if (formData.hasOtherProperty === "no" && (formData.otherProperties || []).length === 0) return

    markTouched("hasOtherProperty")
    markTouched("otherProperties")
    setFormAndFlush((prev) => ({
      ...prev,
      hasOtherProperty: "no",
      otherProperties: [],
    }))
  }, [isPurchase, formData.isFirstHomeBuyer])

  // --------------------------------
  // When user selects "Yes" for other property, auto-create first row
  // --------------------------------
  const ensureAtLeastOneOtherProperty = useCallback(() => {
    setFormAndFlush((prev) => {
      const arr = Array.isArray(prev.otherProperties) ? prev.otherProperties : []
      if (arr.length) return prev
      return { ...prev, otherProperties: [blankOtherProperty()] }
    })
  }, [])

  // --------------------------------
  // PURCHASE CALCS
  // --------------------------------
  const purchasePriceNum = toNumberSafe(formData.purchasePrice)
  const depositNum = toNumberSafe(formData.depositAmount)
  const loanNum = Math.max(0, purchasePriceNum - depositNum)

  const depositPct = purchasePriceNum > 0 ? (depositNum / purchasePriceNum) * 100 : 0
  const lvrPct = purchasePriceNum > 0 ? (loanNum / purchasePriceNum) * 100 : 0

  const isInvestment = formData.occupancy === "investment"
  const minDepositPct = isInvestment ? 10 : 5
  const maxPurchaseLvrPct = isInvestment ? 90 : 95

  const depositStatus = useMemo(() => {
    if (!purchasePriceNum || depositNum <= 0) return null
    if (depositPct < minDepositPct) return { tone: "bad", icon: "🚨", title: "Too low", detail: `Below ${minDepositPct}% deposit` }
    if (depositPct < 20) return { tone: "good", icon: "✅", title: "Good", detail: `${minDepositPct}%+ deposit` }
    return { tone: "great", icon: "🏆", title: "Super good", detail: "20%+ deposit" }
  }, [purchasePriceNum, depositNum, depositPct, minDepositPct])

  const lvrStatus = useMemo(() => {
    if (!purchasePriceNum || loanNum <= 0) return null
    if (lvrPct <= 80) return { tone: "great", icon: "🏆", title: "Super good", detail: "≤ 80% LVR" }
    if (lvrPct <= maxPurchaseLvrPct) return { tone: "good", icon: "✅", title: "Good", detail: `≤ ${maxPurchaseLvrPct}% LVR` }
    return { tone: "bad", icon: "🚨", title: "High", detail: `> ${maxPurchaseLvrPct}% LVR` }
  }, [purchasePriceNum, loanNum, lvrPct, maxPurchaseLvrPct])

  // --------------------------------
  // REFINANCE CALCS
  // --------------------------------
  const refValueNum = toNumberSafe(formData.propertyValue)
  const refBalanceNum = toNumberSafe(formData.loanBalance)
  const refLvrPct = refValueNum > 0 ? (refBalanceNum / refValueNum) * 100 : 0

  const refinanceLvrStatus = useMemo(() => {
    if (!refValueNum || refBalanceNum <= 0) return null
    if (refLvrPct <= 70) return { tone: "great", icon: "🏆", title: "Better rate zone", detail: "≤ 70% LVR" }
    if (refLvrPct <= 80) return { tone: "good", icon: "✅", title: "Good", detail: "≤ 80% LVR" }
    if (refLvrPct <= 90) return { tone: "warn", icon: "⚠️", title: "LMI likely", detail: "> 80% LVR" }
    return { tone: "bad", icon: "🚨", title: "Too high", detail: "> 90% LVR (max)" }
  }, [refValueNum, refBalanceNum, refLvrPct])

  // --------------------------------
  // UI Helpers
  // --------------------------------
  const ui = useMemo(() => {
    const ACCENT = "#4EDCF6"
    const DARK = "#141414"
    const CARD_BG = "rgba(20,20,20,0.04)"

    const W_TITLE = 650
    const W_LABEL = 650
    const W_TEXT = 600
    const W_MUTED = 550

    const page = { maxWidth: 920 }
    const h2 = { marginBottom: 12, fontSize: 22, fontWeight: W_TITLE, color: DARK, letterSpacing: "0.1px" }
    const sectionTitle = { margin: 0, marginBottom: 12, fontSize: 22, fontWeight: W_TITLE, color: DARK, letterSpacing: "0.1px" }

    const label = { display: "block", marginBottom: 10, marginTop: 18, fontSize: 18, fontWeight: W_LABEL, color: DARK }

    const helperBox = {
      marginTop: 10,
      padding: 14,
      borderRadius: 14,
      background: "rgba(20,20,20,0.03)",
      border: "1px solid rgba(20,20,20,0.10)",
      color: "rgba(20,20,20,0.85)",
      lineHeight: 1.45,
      fontSize: 14,
      fontWeight: W_MUTED,
    }

    const sectionCard = {
      marginTop: 12,
      padding: 14,
      borderRadius: 16,
      border: "1px solid rgba(20,20,20,0.10)",
      background: "#ffffff",
      boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
    }

    const sectionCardNoBorder = { marginTop: 12, padding: 0, borderRadius: 0, border: "none", background: "transparent" }
    const fieldLabel = { display: "block", marginBottom: 8, fontSize: 16, fontWeight: W_LABEL, color: DARK }
    const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }

    const input = {
      width: "100%",
      padding: "10px 12px",
      height: 50,
      borderRadius: 14,
      border: "1px solid rgba(20,20,20,0.18)",
      background: "#fff",
      fontSize: 16,
      fontWeight: W_TEXT,
      outline: "none",
      boxShadow: "none",
      minWidth: 0,
      color: "rgba(20,20,20,0.92)",
    }

    const adornContainer = { position: "relative", width: "100%", height: 50 }

    const adornLeft = {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 16,
      fontWeight: W_TEXT,
      color: "rgba(20,20,20,0.65)",
      pointerEvents: "none",
      lineHeight: 1,
      zIndex: 2,
    }

    const adornRight = {
      position: "absolute",
      right: 14,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 16,
      fontWeight: W_TEXT,
      color: "rgba(20,20,20,0.65)",
      pointerEvents: "none",
      lineHeight: 1,
      zIndex: 2,
    }

    const inputWithLeftAdorn = { ...input, paddingLeft: 40 }
    const inputWithRightAdorn = { ...input, paddingRight: 40 }

    const cardWrap = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }

    const card = (selected) => ({
      width: "100%",
      minHeight: 60,
      padding: "0 18px",
      borderRadius: 12,
      border: selected ? "2px solid #141414" : "1.5px solid rgba(20,20,20,0.14)",
      background: selected ? CARD_BG : "#FFFFFF",
      boxShadow: selected ? "0 8px 18px rgba(17,17,17,0.06)" : "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      userSelect: "none",
      transition: "all 0.16s ease",
    })

    const cardText = {
      fontSize: 16,
      fontWeight: 600,
      color: DARK,
      letterSpacing: "-0.01em",
      lineHeight: 1.2,
    }

    const radioOuter = (selected) => ({
      width: 20,
      height: 20,
      borderRadius: 999,
      border: selected ? "2px solid #141414" : "2px solid rgba(20,20,20,0.42)",
      display: "grid",
      placeItems: "center",
      background: "#fff",
      marginRight: 12,
      flex: "0 0 auto",
    })

    const radioInner = (selected) => ({
      width: 10,
      height: 10,
      borderRadius: 999,
      background: selected ? "#141414" : "transparent",
    })

    const left = { display: "flex", alignItems: "center", minWidth: 0 }

    const verifiedBadge = {
      background: "rgba(78,220,246,0.12)",
      padding: "8px 12px",
      borderRadius: 12,
      color: "rgba(20,20,20,0.85)",
      fontSize: 13,
      fontWeight: W_LABEL,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      marginTop: 10,
      border: "1px solid rgba(78,220,246,0.28)",
    }

    const selectStyles = {
      control: (base) => ({
        ...base,
        minHeight: 50,
        height: 50,
        borderRadius: 14,
        border: "1px solid rgba(20,20,20,0.18)",
        boxShadow: "none",
        backgroundColor: "#fff",
        fontSize: 16,
        fontWeight: W_TEXT,
      }),
      valueContainer: (base) => ({ ...base, height: 50, padding: "0 12px" }),
      input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 16, fontWeight: W_TEXT, color: "#141414" }),
      singleValue: (base) => ({ ...base, fontSize: 16, fontWeight: W_TEXT, color: "#141414" }),
      placeholder: (base) => ({ ...base, fontSize: 16, fontWeight: W_TEXT, color: "rgba(20,20,20,0.45)" }),
      indicatorsContainer: (base) => ({ ...base, height: 50 }),
      dropdownIndicator: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
      clearIndicator: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
      menu: (base) => ({ ...base, borderRadius: 14, overflow: "hidden" }),
    }

    const statusRow = { marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }

    const toneBg = (tone) => {
      if (tone === "great") return "rgba(78,220,246,0.18)"
      if (tone === "good") return "rgba(78,220,246,0.12)"
      if (tone === "warn") return "rgba(245,158,11,0.12)"
      return "rgba(239,68,68,0.10)"
    }

    const statusCard = (tone) => ({
      padding: "12px 14px",
      borderRadius: 14,
      border: "1px solid rgba(20,20,20,0.12)",
      background: toneBg(tone),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      fontWeight: W_TEXT,
    })

    const statusLeft = { display: "flex", alignItems: "center", gap: 10, minWidth: 0 }
    const statusTitle = { fontSize: 13, fontWeight: W_LABEL, color: DARK, whiteSpace: "nowrap" }
    const statusDetail = { fontSize: 12, fontWeight: W_MUTED, opacity: 0.8, whiteSpace: "nowrap" }
    const statusRight = { fontSize: 13, fontWeight: W_LABEL, color: DARK }

    const statusRowSingle = { marginTop: 12, display: "grid", gridTemplateColumns: "1fr", gap: 12 }

    const miniBtn = {
      border: "1px solid rgba(20,20,20,0.12)",
      background: "#fff",
      borderRadius: 12,
      padding: "10px 12px",
      cursor: "pointer",
      fontWeight: 800,
      whiteSpace: "nowrap",
    }

    const removeBtn = { border: "none", background: "transparent", cursor: "pointer", fontWeight: 800 }

    const mutedText = {
      opacity: 0.6,
      marginTop: 12,
      fontWeight: 600,
      color: "rgba(20,20,20,0.78)",
    }

    return {
      page,
      h2,
      sectionTitle,
      label,
      helperBox,
      sectionCard,
      sectionCardNoBorder,
      fieldLabel,
      grid2,
      input,
      adornContainer,
      adornLeft,
      adornRight,
      inputWithLeftAdorn,
      inputWithRightAdorn,
      cardWrap,
      card,
      cardText,
      radioOuter,
      radioInner,
      left,
      verifiedBadge,
      selectStyles,
      statusRow,
      statusRowSingle,
      statusCard,
      statusLeft,
      statusTitle,
      statusDetail,
      statusRight,
      miniBtn,
      removeBtn,
      mutedText,
    }
  }, [])

  const addrVerified = !!formData.propertyAddress?.formatted && !!formData.propertyAddress?.place_id
  const shouldAskAddress = isRefinance || (isPurchase && formData.foundProperty === "yes")
  const shouldShowOtherPropertyQuestion =
    isRefinance || (isPurchase && formData.isFirstHomeBuyer === "no")

  const CardRadioGroup = ({ value, onChange, options }) => (
    <div
      style={{
        ...ui.cardWrap,
        gridTemplateColumns: options.length === 1 ? "1fr" : `repeat(${Math.min(options.length, 2)}, minmax(0, 1fr))`,
      }}
    >
      {options.map((opt) => {
        const selected = value === opt.value
        return (
          <div
            key={opt.value}
            role="button"
            tabIndex={0}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onChange(opt.value)
            }}
            style={ui.card(selected)}
          >
            <div style={ui.left}>
              <div style={ui.radioOuter(selected)}>
                <div style={ui.radioInner(selected)} />
              </div>
              <div style={ui.cardText}>{opt.label}</div>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div style={ui.page}>
      <h2 style={ui.h2}>What are you looking to do?</h2>

      <CardRadioGroup
        value={formData.applicationType}
        onChange={(v) => {
          markTouched("applicationType")
          markTouched("transactionType")

          const next = {
            ...formData,
            applicationType: v,
            transactionType: mapAppTypeToMercuryTransactionType(v) || formData.transactionType || "",
            foundProperty: formData.foundProperty || "",
            isFirstHomeBuyer: formData.isFirstHomeBuyer || "",
            occupancy: formData.occupancy || "",
          }
          setFormData(next)
          flushSync(next)
        }}
        options={[
          { value: "purchase", label: "Purchase" },
          { value: "refinance", label: "Refinance" },
        ]}
      />

      {!formData.applicationType && <div style={ui.mutedText}>Select an application type.</div>}

      {!!formData.applicationType && (
        <>
          {isPurchase && (
            <>
              <div style={ui.label}>Have you found a property?</div>

              <CardRadioGroup
                value={formData.foundProperty}
                onChange={handleFoundPropertyChange}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No, still looking" },
                ]}
              />

              {formData.foundProperty === "no" && (
                <div style={ui.helperBox}>
                  If you haven’t found a property yet, you’ll be applying for a{" "}
                  <strong style={{ fontWeight: 650 }}>pre-approval</strong>. This means we’ll assess your finances and give you a decision up
                  front.
                </div>
              )}

              <div style={ui.label}>Are you a first home buyer?</div>
              <CardRadioGroup
                value={formData.isFirstHomeBuyer}
                onChange={(v) => {
                  markTouched("isFirstHomeBuyer")
                  const next = { ...formData, isFirstHomeBuyer: v }
                  setFormData(next)
                  flushSync(next)
                }}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
              />
            </>
          )}

          {(isPurchase || isRefinance) && (
            <>
              <div style={ui.label}>How will the property be used?</div>

              <CardRadioGroup
                value={formData.occupancy}
                onChange={(v) => {
                  markTouched("occupancy")
                  const next = { ...formData, occupancy: v }
                  if (v !== "investment") next.rentalIncomeWeekly = ""
                  setFormData(next)
                  flushSync(next)
                }}
                options={[
                  { value: "livein", label: "Live-in" },
                  { value: "investment", label: "Investment" },
                ]}
              />

              {formData.occupancy === "investment" && (
                <div style={ui.sectionCard}>
                  <label style={ui.fieldLabel}>Estimated weekly rent ($)</label>
                  <div style={ui.adornContainer}>
                    <span style={ui.adornLeft}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={15}
                      style={ui.inputWithLeftAdorn}
                      placeholder="0"
                      value={formatThousands(formData.rentalIncomeWeekly)}
                      onChange={(e) => setField("rentalIncomeWeekly", digitsOnly(e.target.value))}
                      onBlur={() => flushSync(formData)}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {shouldAskAddress && (
            <>
              <div style={ui.label}>Property Address</div>

              <div style={{ position: "relative", marginBottom: 8 }}>
                <GooglePlacesAutocomplete
                  apiKey="AIzaSyAUjzKOLtrcZlyh0l_XqZdrvYSlwOE_hNg"
                  selectProps={{
                    value: addressSelectValue,
                    onChange: handleAddressSelect,
                    placeholder: "Search address…",
                    styles: ui.selectStyles,
                    isClearable: true,
                    onBlur: () => flushSync(formData),
                    onMenuClose: () => flushSync(formData),
                    onChangeRaw: undefined,
                  }}
                />
              </div>

              {addrVerified && (
                <div style={ui.verifiedBadge}>
                  <span>✓</span>
                  <span>Address Verified</span>
                </div>
              )}
            </>
          )}

          {isPurchase && (
            <div style={{ marginTop: 18 }}>
              <h3 style={ui.sectionTitle}>Purchase Details</h3>

              <div style={ui.sectionCard}>
                <div style={ui.grid2}>
                  <div style={ui.adornContainer}>
                    <span style={ui.adornLeft}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={15}
                      style={ui.inputWithLeftAdorn}
                      placeholder="Purchase Price"
                      value={formatThousands(formData.purchasePrice)}
                      onChange={(e) => setField("purchasePrice", digitsOnly(e.target.value))}
                      onBlur={() => flushSync(formData)}
                    />
                  </div>

                  <div style={ui.adornContainer}>
                    <span style={ui.adornLeft}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={15}
                      style={ui.inputWithLeftAdorn}
                      placeholder="Deposit Amount"
                      value={formatThousands(formData.depositAmount)}
                      onChange={(e) => {
                        const v = digitsOnly(e.target.value)
                        markTouched("depositAmount")
                        setFormData((prev) => ({ ...prev, depositAmount: v }))
                        pushDepositAssetToParent(v)
                      }}
                      onBlur={() => flushSync(formData)}
                    />
                  </div>
                </div>
              </div>

              {purchasePriceNum > 0 && (depositNum > 0 || loanNum > 0) && (
                <div style={ui.statusRow}>
                  <div style={ui.statusCard(depositStatus?.tone || "warn")}>
                    <div style={ui.statusLeft}>
                      <span style={{ fontSize: 18 }}>{depositStatus?.icon || "ℹ️"}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={ui.statusTitle}>Deposit</div>
                        <div style={ui.statusDetail}>
                          {minDepositPct}%+ deposit • ${depositNum.toLocaleString("en-AU")}
                        </div>
                      </div>
                    </div>
                    <div style={ui.statusRight}>{depositPct ? `${depositPct.toFixed(2)}%` : "—"}</div>
                  </div>

                  <div style={ui.statusCard(lvrStatus?.tone || "warn")}>
                    <div style={ui.statusLeft}>
                      <span style={{ fontSize: 18 }}>{lvrStatus?.icon || "ℹ️"}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={ui.statusTitle}>LVR</div>
                        <div style={ui.statusDetail}>
                          ≤ {maxPurchaseLvrPct}% LVR • Loan ${loanNum.toLocaleString("en-AU")}
                        </div>
                      </div>
                    </div>
                    <div style={ui.statusRight}>{lvrPct ? `${lvrPct.toFixed(2)}%` : "—"}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {isRefinance && (
            <div style={{ marginTop: 18 }}>
              <h3 style={ui.sectionTitle}>Refinance Details</h3>

              <div style={ui.sectionCardNoBorder}>
                <div style={ui.grid2}>
                  <div style={ui.adornContainer}>
                    <span style={ui.adornLeft}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={15}
                      style={ui.inputWithLeftAdorn}
                      placeholder="Estimated Value"
                      value={formatThousands(formData.propertyValue)}
                      onChange={(e) => setField("propertyValue", digitsOnly(e.target.value))}
                      onBlur={() => flushSync(formData)}
                    />
                  </div>

                  <div style={ui.adornContainer}>
                    <span style={ui.adornLeft}>$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={15}
                      style={ui.inputWithLeftAdorn}
                      placeholder="Loan Balance"
                      value={formatThousands(formData.loanBalance)}
                      onChange={(e) => setField("loanBalance", digitsOnly(e.target.value))}
                      onBlur={() => flushSync(formData)}
                    />
                  </div>

                  <div style={ui.adornContainer}>
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      maxLength={6}
                      style={ui.inputWithRightAdorn}
                      placeholder="Interest Rate"
                      value={formData.interestRate}
                      onChange={(e) => setField("interestRate", percentSanitize(e.target.value))}
                      onBlur={() => flushSync(formData)}
                    />
                    <span style={ui.adornRight}>%</span>
                  </div>

                  <input
                    type="text"
                    placeholder="Current Lender"
                    style={ui.input}
                    value={formData.currentLender}
                    onChange={(e) => setField("currentLender", e.target.value)}
                    onBlur={() => flushSync(formData)}
                  />
                </div>
              </div>

              {refValueNum > 0 && refBalanceNum > 0 && (
                <div style={ui.statusRowSingle}>
                  <div style={ui.statusCard(refinanceLvrStatus?.tone || "warn")}>
                    <div style={ui.statusLeft}>
                      <span style={{ fontSize: 18 }}>{refinanceLvrStatus?.icon || "ℹ️"}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={ui.statusTitle}>LVR</div>
                        <div style={ui.statusDetail}>
                          {refinanceLvrStatus?.detail || "Balance ÷ Value"} • Loan ${refBalanceNum.toLocaleString("en-AU")}
                        </div>
                      </div>
                    </div>
                    <div style={ui.statusRight}>{refLvrPct ? `${refLvrPct.toFixed(2)}%` : "—"}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {shouldShowOtherPropertyQuestion && (
            <>
              <div style={ui.label}>Do you own any other property?</div>

              <CardRadioGroup
                value={formData.hasOtherProperty}
                onChange={(v) => {
                  markTouched("hasOtherProperty")
                  markTouched("otherProperties")

                  if (v === "yes") {
                    const next = {
                      ...formData,
                      hasOtherProperty: "yes",
                      otherProperties: Array.isArray(formData.otherProperties) && formData.otherProperties.length
                        ? formData.otherProperties
                        : [blankOtherProperty()],
                    }
                    setFormData(next)
                    flushSync(next)
                    return
                  }

                  const next = { ...formData, hasOtherProperty: "no", otherProperties: [] }
                  setFormData(next)
                  flushSync(next)
                }}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
              />

              {formData.hasOtherProperty === "yes" && (
                <div style={ui.sectionCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div style={{ fontWeight: 800 }}>Other property addresses</div>
                    <button
                      type="button"
                      onClick={() => {
                        ensureAtLeastOneOtherProperty()
                        addOtherProperty()
                      }}
                      style={ui.miniBtn}
                    >
                      + Add another property
                    </button>
                  </div>

                  {(formData.otherProperties || []).length === 0 && (
                    <div style={{ marginTop: 10, opacity: 0.75, fontWeight: 650 }}>
                      Add at least one property if you own other property.
                    </div>
                  )}

                  {(formData.otherProperties || []).map((p, idx) => {
                    const verified = !!p?.address?.formatted && !!p?.address?.place_id
                    const selectValue = p?.address?.formatted
                      ? {
                          label: p.address.formatted,
                          value: { description: p.address.formatted, place_id: p.address.place_id || null },
                        }
                      : null

                    return (
                      <div
                        key={idx}
                        style={{
                          marginTop: 14,
                          padding: 14,
                          borderRadius: 14,
                          border: "1px solid rgba(20,20,20,0.12)",
                          background: "#fff",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                          <div style={{ fontWeight: 800 }}>Property {idx + 1}</div>
                          {(formData.otherProperties || []).length > 1 && (
                            <button type="button" onClick={() => removeOtherProperty(idx)} style={ui.removeBtn}>
                              ❌ Remove
                            </button>
                          )}
                        </div>

                        <div style={{ marginTop: 12 }}>
                          <div style={ui.fieldLabel}>Other property address</div>
                          <GooglePlacesAutocomplete
                            apiKey="AIzaSyAUjzKOLtrcZlyh0l_XqZdrvYSlwOE_hNg"
                            selectProps={{
                              value: selectValue,
                              placeholder: "Search address…",
                              styles: ui.selectStyles,
                              isClearable: true,
                              onChange: async (selected) => {
                                if (!selected) {
                                  patchOtherProperty(idx, { address: null })
                                  return
                                }

                                const placeId = selected?.value?.place_id
                                if (!placeId) {
                                  patchOtherProperty(idx, { address: { formatted: selected.label } })
                                  return
                                }

                                try {
                                  const results = await geocodeByPlaceId(placeId)
                                  const r = results[0]
                                  const components = r.address_components || []
                                  const get = (t) => components.find((c) => c.types?.includes(t))?.long_name || ""

                                  const structured = {
                                    formatted: r.formatted_address,
                                    place_id: placeId,
                                    street: `${get("street_number")} ${get("route")}`.trim(),
                                    suburb: get("locality") || get("sublocality") || get("administrative_area_level_2"),
                                    state: get("administrative_area_level_1"),
                                    postcode: get("postal_code"),
                                    lat: r.geometry?.location?.lat?.(),
                                    lng: r.geometry?.location?.lng?.(),
                                  }

                                  patchOtherProperty(idx, { address: structured })
                                } catch (e) {
                                  patchOtherProperty(idx, { address: { formatted: selected.label, place_id: placeId } })
                                }
                              },
                              onBlur: () => flushSync(formData),
                              onMenuClose: () => flushSync(formData),
                            }}
                          />

                          {verified && (
                            <div style={ui.verifiedBadge}>
                              <span>✓</span>
                              <span>Address Verified</span>
                            </div>
                          )}
                        </div>

                        <div style={{ marginTop: 14 }}>
                          <div style={ui.fieldLabel}>Do you earn rental income from this property?</div>

                          <CardRadioGroup
                            value={p?.hasRentalIncome || ""}
                            onChange={(v) => {
                              patchOtherProperty(idx, {
                                hasRentalIncome: v,
                                rentalIncomeMonthly: v === "yes" ? p?.rentalIncomeMonthly || "" : "",
                              })
                            }}
                            options={[
                              { value: "yes", label: "Yes" },
                              { value: "no", label: "No" },
                            ]}
                          />

                          {p?.hasRentalIncome === "yes" && (
                            <div style={{ marginTop: 12 }}>
                              <label style={ui.fieldLabel}>Monthly rent ($)</label>
                              <div style={ui.adornContainer}>
                                <span style={ui.adornLeft}>$</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  maxLength={15}
                                  style={ui.inputWithLeftAdorn}
                                  placeholder="0"
                                  value={formatThousands(p?.rentalIncomeMonthly || "")}
                                  onChange={(e) => patchOtherProperty(idx, { rentalIncomeMonthly: digitsOnly(e.target.value) })}
                                  onBlur={() => flushSync(formData)}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default PropertyStep